import {useCallback, useMemo} from 'react';
import {useQueries, useQueryClient} from '@tanstack/react-query';
import {FilesService} from '@services/Files/FilesService';
import {useAppDispatch, useAppSelector} from 'slices/hooks';
import {addReportTask, updateReportStatus} from 'slices/reports';
import {downloadAndExtractFileExtension} from 'utils/api/file';
import {ReportData, ReportGenerationInfo} from '../types';
import {reportTypeList} from '../reportTypeList';
import i18n from 'i18n';

const filesService = FilesService.getInstance();

type StartReportFn = (
    reportType: keyof typeof reportTypeList,
    queryParams: Record<string, any>,
    body: Record<string, any>,
) => Promise<ReportGenerationInfo | void>;

export function useReportsManager() {
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();
    const reports = useAppSelector((state) => state.reports.tasks);

    const startReport: StartReportFn = useCallback(
        async (reportType, queryParams, body) => {
            try {
                const result = await filesService.queryToRunReportProcessing(
                    reportType,
                    queryParams,
                    body,
                );

                if (result?.id) {
                    const reportTitle = reportTypeList[reportType];
                    dispatch(
                        addReportTask({
                            id: result.id,
                            title: reportTitle,
                            requestedAt: new Date().toISOString(),
                            warnings: [],
                        }),
                    );

                    dispatch(
                        updateReportStatus({
                            id: result.id,
                            status: 'IN_PROGRESS',
                            warnings: [],
                        }),
                    );
                }

                return result;
            } catch (error: any) {
                dispatch(
                    updateReportStatus({
                        id: '',
                        status: 'ERROR',
                        warnings: [],
                    }),
                );
                throw error;
            }
        },
        [dispatch],
    );

    const inProgressReports = useMemo(
        () => reports.filter((r) => r.status === 'IN_PROGRESS'),
        [reports],
    );

    // пулим запросы для каждого отчета
    const reportStatuses = useQueries({
        queries: inProgressReports.map((r) => ({
            queryKey: ['report-status', r.id],
            queryFn: async () => {
                try {
                    const response = await filesService.getReportStatus(r.id, true);
                    const currentData = queryClient.getQueryData<ReportData>([
                        'report-status',
                        r.id,
                    ]);
                    if (!currentData || currentData.status !== response.status) {
                        queryClient.setQueryData(['report-status', r.id], response);
                    }

                    dispatch(
                        updateReportStatus({
                            id: r.id,
                            status: response.status,
                            warnings: response.warnings || [],
                        }),
                    );

                    if (response.status === 'DONE') {
                        downloadReadyReport(r.id);
                    }

                    return response;
                } catch (error: any) {
                    dispatch(
                        updateReportStatus({
                            id: r.id,
                            status: 'ERROR',
                            warnings: [],
                        }),
                    );
                    throw error;
                }
            },
            retry: false,
            meta: {withoutGlobalLoader: true},
            refetchInterval: (query: any) => {
                if (query.state.error) {
                    return false;
                }
                if (!query.state.data || query.state.data.status === 'IN_PROGRESS') {
                    return 3000;
                }
                return false;
            },
        })),
    });

    const handleDownloadError = (reportId: string, error: any) => {
        const status = error.response?.status;
        let warningMessage = i18n.t('useReportsManager.errorLoadingReportGenerateNew', {ns: 'supervising'});

        if (status === 404) {
            warningMessage = i18n.t('useReportsManager.reportNotFoundItMayDeleted', {ns: 'supervising'});
        }

        dispatch(
            updateReportStatus({
                id: reportId,
                status: 'ERROR',
                warnings: [warningMessage],
            }),
        );
    };

    const downloadReadyReport = async (reportId: string) => {
        try {
            const file = await filesService.downloadFile(reportId);
            downloadAndExtractFileExtension(file);
        } catch (error: any) {
            handleDownloadError(reportId, error);
        }
    };

    return {
        reports,
        startReport,
        downloadReadyReport,
        reportStatuses,
    };
}
